import { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Flex, Text, VStack, HStack, Spinner, Button, Input, Select, Textarea,
    Badge, Image, useToast, SimpleGrid, Divider, Icon, Tabs, TabList, TabPanels,
    Tab, TabPanel, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
    ModalCloseButton, useDisclosure, Tooltip, Wrap, WrapItem,
    InputGroup, InputLeftElement, InputRightElement, CloseButton, Link
} from "@chakra-ui/react";
import {
    ShieldCheck, Search, Truck, RefreshCw, AlertTriangle, CheckCircle2,
    HelpCircle, XCircle, Ban, Clock, FileSearch, ExternalLink, ShoppingCart
} from "lucide-react";
import {
    getCompatibilidadeCatalogos, postCompatibilidadeVerificar,
    getCompatibilidadeSolicitacao, getCompatibilidadeHistorico,
    getCompatibilidadeEcommerce, postCompatibilidadeEcommerceVerificar,
    getCompatibilidadeSolicitacaoPedido, getCompatibilidadeMercadoLivre,
    postCompatibilidadeMercadoLivreVerificar
} from "../services/api";
import { baseUrlApi } from "../config";

const STORAGE_BASE = baseUrlApi.replace(/\/api$/, "");

// Reescreve a URL do anexo (o backend pode devolver com host errado — mantém só o path).
const resolverUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) {
        try { const parsed = new URL(url); return STORAGE_BASE + parsed.pathname; }
        catch { return url; }
    }
    return STORAGE_BASE + url;
};

// ── Polling da solicitação (mesmo padrão do chat-ia: falha isolada não aborta) ──
const POLL_INTERVALO_MS = 3000;
const POLL_MAX_MS = 18 * 60 * 1000;
const POLL_FALHAS_SEGUIDAS_MAX = 10;

const aguardarSolicitacao = async (id) => {
    const inicio = Date.now();
    let falhas = 0;
    while (Date.now() - inicio < POLL_MAX_MS) {
        await new Promise(r => setTimeout(r, POLL_INTERVALO_MS));
        const res = await getCompatibilidadeSolicitacao(id);
        if (!res || !res.status) {
            if (++falhas >= POLL_FALHAS_SEGUIDAS_MAX) {
                return { status: "erro", erro: "Não foi possível consultar o andamento.", tipo_erro: "poll_falhou" };
            }
            continue;
        }
        falhas = 0;
        if (res.status === "concluida" || res.status === "erro") return res;
    }
    return { status: "erro", erro: "Tempo limite de acompanhamento.", tipo_erro: "poll_timeout" };
};

// Veredito → rótulo, cor e ícone.
const VEREDITOS = {
    COMPATIVEL:     { label: "Compatível",       cor: "green",  Icon: CheckCircle2 },
    INCOMPATIVEL:   { label: "Incompatível",     cor: "red",    Icon: XCircle },
    NAO_CONFIRMADO: { label: "Não confirmado",   cor: "orange", Icon: HelpCircle },
    FORA_COBERTURA: { label: "Fora de cobertura", cor: "gray",  Icon: Ban },
    SEM_VEICULO:    { label: "Sem veículo",      cor: "gray",   Icon: Ban },
    INDETERMINADO:  { label: "Indeterminado",    cor: "purple", Icon: HelpCircle },
};
const vinfo = (v) => VEREDITOS[v] || { label: v || "—", cor: "gray", Icon: HelpCircle };

const formatarData = (d) => {
    if (!d) return "";
    const dt = new Date(d.replace(" ", "T"));
    if (isNaN(dt)) return d;
    return dt.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

// Remove a 1ª linha "VEREDITO: X" do corpo (mostramos como badge).
const corpoSemVeredito = (txt) => (txt || "").replace(/^\s*VEREDITO:.*\n?/i, "").trim();

// Badge do status/veredito.
const StatusBadge = ({ status, veredito }) => {
    if (status === "pendente" || status === "processando") {
        return <Badge colorScheme="blue" display="flex" alignItems="center" gap={1}><Spinner size="xs" /> Processando</Badge>;
    }
    if (status === "erro") return <Badge colorScheme="red">Erro</Badge>;
    const vi = vinfo(veredito);
    return <Badge colorScheme={vi.cor} display="flex" alignItems="center" gap={1}><Icon as={vi.Icon} boxSize={3} /> {vi.label}</Badge>;
};

// Renderiza o resultado detalhado + vistas explodidas.
const ResultadoDetalhe = ({ sol }) => {
    if (!sol) return null;
    if (sol.status === "erro") {
        return (
            <Box p={4} bg="red.50" borderRadius="md" borderWidth="1px" borderColor="red.200">
                <HStack color="red.600"><Icon as={AlertTriangle} /><Text fontWeight="bold">Falha na verificação</Text></HStack>
                <Text mt={1} fontSize="sm" color="red.700">{sol.erro || "Erro desconhecido."}</Text>
            </Box>
        );
    }
    const vi = vinfo(sol.veredito);
    const anexos = sol.anexos || [];
    return (
        <Box>
            <HStack mb={3} spacing={3} flexWrap="wrap">
                <Badge colorScheme={vi.cor} fontSize="md" px={3} py={1} borderRadius="md" display="flex" alignItems="center" gap={2}>
                    <Icon as={vi.Icon} boxSize={4} /> {vi.label}
                </Badge>
                {sol.observacao_salva && <Badge colorScheme="teal">Nota salva no pedido</Badge>}
                {sol.concluido_em && <Text fontSize="xs" color="gray.500">{formatarData(sol.concluido_em)}</Text>}
            </HStack>

            {anexos.length > 0 && (
                <Wrap mb={4} spacing={3}>
                    {anexos.map((a, i) => (
                        <WrapItem key={i}>
                            <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" cursor="pointer"
                                 onClick={() => window.open(resolverUrl(a.url), "_blank")}>
                                <Image src={resolverUrl(a.url)} alt={a.nome_original || "Vista explodida"}
                                       maxH="260px" objectFit="contain" bg="white" />
                                <Text fontSize="10px" px={2} py={1} bg="gray.50" noOfLines={1}>
                                    <Icon as={ExternalLink} boxSize={2.5} mr={1} />{a.nome_original || "Vista explodida"}
                                </Text>
                            </Box>
                        </WrapItem>
                    ))}
                </Wrap>
            )}

            {Array.isArray(sol.oem_sugerido) && sol.oem_sugerido.length > 0 && (
                <Box mb={4} bg="blue.50" borderRadius="md" p={3} borderWidth="1px" borderColor="blue.200">
                    <HStack mb={2} color="blue.700">
                        <Icon as={AlertTriangle} boxSize={4} />
                        <Text fontWeight="bold" fontSize="sm">Número que o catálogo usa nessa posição</Text>
                    </HStack>
                    {sol.oem_sugerido.map((o, i) => (
                        <Text key={i} fontSize="sm" mb={1}>
                            <b>{o.numero}</b>
                            {o.peca ? ` (peça ${o.peca})` : ""}
                            {o.descricao ? ` — ${o.descricao}` : ""}
                        </Text>
                    ))}
                    <Text fontSize="xs" color="blue.700" mt={2}>
                        Não é equivalência confirmada: o catálogo mostra esse número no lugar da peça
                        pedida, mas não afirma que ele substitui o código consultado. Confira antes de oferecer.
                    </Text>
                </Box>
            )}

            <Box bg="gray.50" borderRadius="md" p={4} borderWidth="1px" borderColor="gray.200">
                <Text whiteSpace="pre-wrap" fontSize="sm" lineHeight="1.6">{corpoSemVeredito(sol.resultado)}</Text>
            </Box>
        </Box>
    );
};

// ── Painel de dados do pedido do e-commerce (modal) ──
const STATUS_PEDIDO = {
    PENDING: "Aguardando pagamento", PAYMENT_CONFIRMED: "Pagamento confirmado",
    PROCESSING: "Em separação", SHIPPED: "Enviado", DELIVERED: "Entregue",
    CANCELLED: "Cancelado", CANCELED: "Cancelado", REFUNDED: "Reembolsado",
};
const METODO_PGTO = { CREDIT_CARD: "Cartão de crédito", DEBIT_CARD: "Cartão de débito", PIX: "PIX", BOLETO: "Boleto" };
const fmtMoeda = (v) => (v == null || v === "" || isNaN(v)) ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const LinhaInfo = ({ label, children }) => (
    <HStack align="start" spacing={2} fontSize="sm">
        <Text color="gray.500" minW="76px" flexShrink={0}>{label}</Text>
        <Box flex={1} minW={0}>{children}</Box>
    </HStack>
);

const PedidoResumo = ({ pedido, loading }) => {
    if (loading) {
        return <HStack py={3} color="gray.500"><Spinner size="sm" /><Text fontSize="sm">Carregando dados do pedido…</Text></HStack>;
    }
    if (!pedido) {
        return (
            <Box p={3} bg="orange.50" borderWidth="1px" borderColor="orange.200" borderRadius="md">
                <Text fontSize="sm" color="orange.700">Não foi possível carregar os dados do pedido no e-commerce agora.</Text>
            </Box>
        );
    }
    const c = pedido.customer || {};
    const a = pedido.address;
    const s = pedido.shipping;
    const pg = pedido.payment;
    const v = pedido.vehicle || {};
    const itens = pedido.items || [];
    const wa = (c.whatsapp || "").replace(/\D/g, "");

    return (
        <Box>
            <Flex justify="space-between" align="start" flexWrap="wrap" gap={2} mb={3}>
                <Box>
                    <Text fontWeight="bold" fontSize="md">{pedido.order_number}</Text>
                    <Text fontSize="xs" color="gray.500">
                        {formatarData(pedido.created_at)} · {STATUS_PEDIDO[pedido.status] || pedido.status || "—"}
                    </Text>
                </Box>
                <Text fontWeight="bold" fontSize="lg" color="green.600">{fmtMoeda(pedido.total)}</Text>
            </Flex>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {/* Cliente */}
                <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2}>CLIENTE</Text>
                    <VStack align="stretch" spacing={1.5}>
                        <LinhaInfo label="Nome">
                            <HStack spacing={2} align="center">
                                <Text fontWeight="medium">{c.name || "—"}</Text>
                                {c.user_type && (
                                    <Badge colorScheme={c.user_type === "COMPANY" ? "purple" : "blue"} fontSize="9px">
                                        {c.user_type === "COMPANY" ? "PJ" : "PF"}
                                    </Badge>
                                )}
                            </HStack>
                        </LinhaInfo>
                        {c.document && <LinhaInfo label={c.doc_tipo || "Doc"}>{c.document}</LinhaInfo>}
                        {c.whatsapp && (
                            <LinhaInfo label="WhatsApp">
                                <Link href={`https://wa.me/${wa}`} isExternal color="blue.500">{c.whatsapp}</Link>
                            </LinhaInfo>
                        )}
                        {c.email && <LinhaInfo label="E-mail"><Text noOfLines={1}>{c.email}</Text></LinhaInfo>}
                    </VStack>
                </Box>

                {/* Entrega e pagamento */}
                <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
                    <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={2}>ENTREGA E PAGAMENTO</Text>
                    <VStack align="stretch" spacing={1.5}>
                        {a && (
                            <LinhaInfo label="Endereço">
                                {[a.street, a.number].filter(Boolean).join(", ")}{a.complement ? ` — ${a.complement}` : ""}
                                <Text color="gray.500" fontSize="xs">
                                    {[a.neighborhood, [a.city, a.state].filter(Boolean).join("/")].filter(Boolean).join(" · ")}
                                    {a.zip ? ` · CEP ${a.zip}` : ""}
                                </Text>
                            </LinhaInfo>
                        )}
                        {s && (
                            <LinhaInfo label="Frete">
                                {[s.carrier, s.service].filter(Boolean).join(" · ") || "—"}{s.days ? ` (${s.days} dia${s.days > 1 ? "s" : ""})` : ""}
                            </LinhaInfo>
                        )}
                        {pg && (
                            <LinhaInfo label="Pagamento">
                                {METODO_PGTO[pg.method] || pg.method || "—"}{pg.installments > 1 ? ` — ${pg.installments}x` : ""}
                            </LinhaInfo>
                        )}
                    </VStack>
                </Box>
            </SimpleGrid>

            {/* Itens + totais */}
            <Box mt={3} borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>ITENS</Text>
                {itens.map((it, i) => (
                    <Flex key={i} py={1.5} borderBottomWidth={i < itens.length - 1 ? "1px" : 0} borderColor="gray.100"
                          justify="space-between" align="start" gap={3}>
                        <Box minW={0}>
                            <Text fontSize="sm" noOfLines={2}>{it.name || "—"}</Text>
                            <Text fontSize="xs" color="gray.500">cód. {it.sku || "—"} · {it.quantity || 1}× {fmtMoeda(it.price)}</Text>
                        </Box>
                        <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">{fmtMoeda(it.total)}</Text>
                    </Flex>
                ))}
                <VStack align="stretch" spacing={0.5} mt={2} fontSize="sm">
                    <Flex justify="space-between" color="gray.600"><Text>Subtotal</Text><Text>{fmtMoeda(pedido.subtotal)}</Text></Flex>
                    <Flex justify="space-between" color="gray.600"><Text>Frete</Text><Text>{fmtMoeda(pedido.shipping_cost)}</Text></Flex>
                    {pedido.discount > 0 && (
                        <Flex justify="space-between" color="green.600"><Text>Desconto</Text><Text>- {fmtMoeda(pedido.discount)}</Text></Flex>
                    )}
                    <Flex justify="space-between" fontWeight="bold" pt={1} borderTopWidth="1px" borderColor="gray.100">
                        <Text>Total</Text><Text>{fmtMoeda(pedido.total)}</Text>
                    </Flex>
                </VStack>
            </Box>

            {/* Veículo informado */}
            <Box mt={3} bg="blue.50" borderWidth="1px" borderColor="blue.100" borderRadius="md" p={3}>
                <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={1}>VEÍCULO INFORMADO PELO CLIENTE</Text>
                <Text fontSize="sm">{[v.brand, v.model, v.year].filter(Boolean).join(" · ") || "não informado"}</Text>
                {v.chassis && <Text fontSize="xs" color="gray.600" mt={0.5}>Chassi: <b>{v.chassis}</b></Text>}
                {v.observations && <Text fontSize="xs" color="gray.600" mt={0.5}>Obs.: {v.observations}</Text>}
            </Box>
        </Box>
    );
};

// Filtros das abas de pedidos (por veredito/estado). "processando" agrupa
// pendente/processando. Só aparecem os que têm resultado na lista.
const FILTROS_PEDIDOS = [
    { key: "todos", label: "Todos" },
    { key: "processando", label: "Processando" },
    { key: "COMPATIVEL", label: "Compatível" },
    { key: "INCOMPATIVEL", label: "Incompatível" },
    { key: "NAO_CONFIRMADO", label: "Não confirmado" },
    { key: "FORA_COBERTURA", label: "Fora de cobertura" },
    { key: "SEM_VEICULO", label: "Sem veículo" },
    { key: "erro", label: "Erro" },
];
const bucketPedido = (e) => (e.status === "pendente" || e.status === "processando") ? "processando"
    : (e.status === "erro" ? "erro" : (e.veredito || "INDETERMINADO"));
const corPedido = (e) => (e.status === "pendente" || e.status === "processando") ? "blue"
    : (e.status === "erro" ? "red" : vinfo(e.veredito).cor);

// Lista de pedidos verificados — e-commerce e Mercado Livre usam a mesma. As
// diferenças do ML: o pedido é identificado pela venda, o resultado pode ter
// TIRADO o pedido de pendente, e o chassi vem de fonte variável (obs, chat ou
// foto lida pela IA) — a equipe precisa ver de onde veio antes de confiar.
const PainelPedidos = ({ lista, loading, verificando, onAtualizar, onVerificar, onAbrir, aviso, vazio, botaoLabel, IconeVazio, ehML }) => {
    const [busca, setBusca] = useState("");
    const [filtro, setFiltro] = useState("todos");

    const termo = busca.trim().toLowerCase();
    const contagem = {};
    lista.forEach(e => { const b = bucketPedido(e); contagem[b] = (contagem[b] || 0) + 1; });
    const filtrada = lista.filter(e => {
        if (filtro !== "todos" && bucketPedido(e) !== filtro) return false;
        if (!termo) return true;
        return [e.order_number, e.veiculo, e.peca, ehML ? `#${e.venda_id}` : ""]
            .some(x => (x || "").toLowerCase().includes(termo));
    });

    return (
        <>
            <Box bg="gray.50" borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3} mb={4}>
                <HStack justify="space-between" flexWrap="wrap" spacing={3}>
                    <Text fontSize="sm" color="gray.600">
                        <Icon as={Clock} boxSize={4} mr={1} verticalAlign="text-bottom" />
                        {aviso}
                    </Text>
                    <HStack>
                        <Button size="sm" variant="outline" leftIcon={<Icon as={RefreshCw} boxSize={4} />} onClick={onAtualizar} isLoading={loading}>
                            Atualizar
                        </Button>
                        <Button size="sm" colorScheme="blue" leftIcon={<Icon as={IconeVazio} boxSize={4} />} onClick={onVerificar} isLoading={verificando} loadingText="Buscando…">
                            {botaoLabel}
                        </Button>
                    </HStack>
                </HStack>
            </Box>

            {lista.length === 0 && !loading && (
                <VStack py={10} color="gray.400" spacing={2}>
                    <Icon as={IconeVazio} boxSize={8} />
                    <Text fontSize="sm" textAlign="center">{vazio}</Text>
                </VStack>
            )}

            {lista.length > 0 && (
                <Box>
                    <Flex gap={3} align="center" flexWrap="wrap" mb={3}>
                        <InputGroup size="sm" maxW="420px" flex="1 1 260px">
                            <InputLeftElement pointerEvents="none"><Icon as={Search} boxSize={4} color="gray.400" /></InputLeftElement>
                            <Input placeholder={ehML ? "Buscar por venda, nº do pedido, veículo ou peça…" : "Buscar por nº do pedido, veículo ou peça…"}
                                   value={busca} onChange={ev => setBusca(ev.target.value)} />
                            {busca && (
                                <InputRightElement><CloseButton size="sm" onClick={() => setBusca("")} /></InputRightElement>
                            )}
                        </InputGroup>
                        <Text fontSize="xs" color="gray.500">
                            {filtrada.length} de {lista.length} pedido(s)
                        </Text>
                    </Flex>

                    <Wrap spacing={2} mb={3}>
                        {FILTROS_PEDIDOS.filter(f => f.key === "todos" || contagem[f.key]).map(f => {
                            const ativo = filtro === f.key;
                            const n = f.key === "todos" ? lista.length : (contagem[f.key] || 0);
                            return (
                                <WrapItem key={f.key}>
                                    <Button size="xs" borderRadius="full" onClick={() => setFiltro(f.key)}
                                            variant={ativo ? "solid" : "outline"} colorScheme={ativo ? "blue" : "gray"}>
                                        {f.label}
                                        <Badge ml={1.5} colorScheme={ativo ? "blue" : "gray"} variant={ativo ? "solid" : "subtle"}
                                               borderRadius="full" fontSize="10px">{n}</Badge>
                                    </Button>
                                </WrapItem>
                            );
                        })}
                    </Wrap>

                    {filtrada.length === 0 && (
                        <Text fontSize="sm" color="gray.400" py={6} textAlign="center">
                            Nenhum pedido corresponde à busca/filtro.
                        </Text>
                    )}

                    <VStack align="stretch" spacing={1.5}>
                        {filtrada.map(e => (
                            <Flex key={e.id} p={3} borderWidth="1px" borderColor="gray.200" borderRadius="md"
                                  borderLeftWidth="3px" borderLeftColor={`${corPedido(e)}.400`} align="center"
                                  justify="space-between" gap={3} _hover={{ bg: "gray.50", borderColor: "gray.300" }}
                                  cursor="pointer" onClick={() => onAbrir(e)}>
                                <Box minW={0}>
                                    <HStack spacing={2} flexWrap="wrap">
                                        <Text fontSize="sm" fontWeight="bold">
                                            {ehML ? `Venda #${e.venda_id}` : e.order_number}
                                        </Text>
                                        {ehML && e.pedido_confirmado && (
                                            <Tooltip label="O catálogo confirmou a peça e o pedido saiu de pendente">
                                                <Badge colorScheme="green" fontSize="9px">pedido confirmado</Badge>
                                            </Tooltip>
                                        )}
                                        {e.observacao_salva && <Tooltip label="Nota salva na venda"><Badge colorScheme="teal" fontSize="9px">nota ✓</Badge></Tooltip>}
                                        {e.pecas_omitidas > 0 && (
                                            <Tooltip label={`O pedido tem mais itens do que cabe numa verificação: ${e.pecas_omitidas} não foram conferidos`}>
                                                <Badge colorScheme="orange" fontSize="9px">{e.pecas_omitidas} não conferido(s)</Badge>
                                            </Tooltip>
                                        )}
                                    </HStack>
                                    <Text fontSize="xs" color="gray.600" noOfLines={1}>{e.veiculo || "sem veículo"} · {e.peca || "—"}</Text>
                                    {ehML && e.fonte_chassi && (
                                        <Text fontSize="xs" color="gray.500" noOfLines={1}>chassi via {e.fonte_chassi}</Text>
                                    )}
                                    <Text fontSize="xs" color="gray.400">
                                        {formatarData(e.created_at)}{ehML && e.order_number ? ` · pedido ${e.order_number}` : ""}
                                    </Text>
                                </Box>
                                <StatusBadge status={e.status} veredito={e.veredito} />
                            </Flex>
                        ))}
                    </VStack>
                </Box>
            )}
        </>
    );
};

const CATALOGOS_FALLBACK = [
    { service: "man_parts", label: "MAN" },
    { service: "mercedestrucks_parts", label: "Mercedes-Benz Caminhões" },
    { service: "mercedesunimog_parts", label: "Mercedes-Benz Unimog" },
    { service: "mercedesvans_parts", label: "Mercedes-Benz Vans (Sprinter)" },
    { service: "busandtrucks_parts", label: "VW Caminhões e Ônibus" },
    { service: "vn_parts", label: "VW Comerciais" },
    { service: "fordt_oic_parts", label: "Ford Trucks" },
    { service: "iveco_oic_parts", label: "Iveco (Daily, Tector, Stralis…)" },
    { service: "scania_multi_parts", label: "Scania (R, P, G, S, K, 113/124…)" },
    { service: "volvo_impact_parts", label: "Volvo (FH, FM, FL, FE — linha atual)" },
    { service: "agrale_catalogo_parts", label: "Agrale / Volare / Marruá (MA, 8500, 9200, V/W, AM…)" },
    { service: "daf_partsrapido_parts", label: "DAF (XF, CF, LF, 95XF…)" },
];

// O catálogo Agrale é o único sem banco de chassis: a confirmação sai em nível
// de MODELO. Espelha o roteamento do backend (AgraleCatalogoService).
const AGRALE = /\b(agrale|volare|marru[aá])\b/i;
const ehAgrale = (form) => form.catalogo === "agrale_catalogo_parts"
    || (!form.catalogo && (AGRALE.test(form.marca) || (!form.marca.trim() && AGRALE.test(form.modelo))));

// O chassi DAF tem ~8 caracteres (não é o VIN de 17) e a edição do catálogo é de
// 2015. Espelha o roteamento do backend (DafCatalogoService).
const DAF = /\bdaf\b/i;
const DAF_MODELO = /\b(XF|CF|LF)\s?-?\s?\d{2,3}\b|\b\d{2,3}\s?-?\s?(XF|CF|LF)\b|\bSB\s?-?\s?\d{3,4}\b/i;
const ehDaf = (form) => form.catalogo === "daf_partsrapido_parts"
    || (!form.catalogo && (DAF.test(form.marca)
        || (!form.marca.trim() && (DAF.test(form.modelo) || DAF_MODELO.test(form.modelo)))));

// Cada catálogo pede o veículo de um jeito — evita ternário aninhado no JSX.
const dicaChassi = (form) => ehAgrale(form) ? "o catálogo Agrale não usa chassi — informe o modelo"
    : ehDaf(form) ? "o chassi DAF tem 8 caracteres, não é o VIN de 17" : "chassi identifica melhor";
const phChassi = (form) => ehAgrale(form) ? "Chassi / VIN (não é usado pelo catálogo Agrale)"
    : ehDaf(form) ? "Chassi DAF (ex.: 0E653487)" : "Chassi / VIN (ex.: 9534B8269AR053932)";
const phModelo = (form) => ehAgrale(form) ? "Modelo (ex.: MA 9.2 Euro V)"
    : ehDaf(form) ? "Modelo (ex.: XF105, CF85IV)" : "Modelo (ex.: Constellation 26-260)";

const FORM_VAZIO = { catalogo: "", chassi: "", marca: "", modelo: "", ano: "", peca_numero: "", peca_nome: "", contexto: "" };

const CompatibilidadeCatalogo = () => {
    const toast = useToast();
    const [catalogos, setCatalogos] = useState(CATALOGOS_FALLBACK);

    // ── Aba manual ──
    const [form, setForm] = useState(FORM_VAZIO);
    const [processando, setProcessando] = useState(false);
    const [resultado, setResultado] = useState(null); // solicitação concluída/erro em foco
    const [historico, setHistorico] = useState([]);
    const solAtivaRef = useRef(null); // id sendo acompanhado (evita aplicar resultado antigo)

    // ── Aba e-commerce ──
    const [ecomLista, setEcomLista] = useState([]);
    const [ecomLoading, setEcomLoading] = useState(false);
    const [ecomVerificando, setEcomVerificando] = useState(false);

    // ── Aba Mercado Livre ──
    const [mlLista, setMlLista] = useState([]);
    const [mlLoading, setMlLoading] = useState(false);
    const [mlVerificando, setMlVerificando] = useState(false);

    // ── Modal de detalhe ──
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [detalhe, setDetalhe] = useState(null);
    const [detalheLoading, setDetalheLoading] = useState(false);
    const [detalheEhEcom, setDetalheEhEcom] = useState(false);
    const [detalhePedido, setDetalhePedido] = useState(null);
    const [detalhePedidoLoading, setDetalhePedidoLoading] = useState(false);

    const setCampo = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const carregarHistorico = useCallback(async () => {
        const res = await getCompatibilidadeHistorico("manual");
        if (res && res.data) setHistorico(res.data);
    }, []);

    const carregarEcommerce = useCallback(async () => {
        setEcomLoading(true);
        const res = await getCompatibilidadeEcommerce();
        if (res && res.data) setEcomLista(res.data);
        setEcomLoading(false);
    }, []);

    const carregarMercadoLivre = useCallback(async () => {
        setMlLoading(true);
        const res = await getCompatibilidadeMercadoLivre();
        if (res && res.data) setMlLista(res.data);
        setMlLoading(false);
    }, []);

    // Retoma o polling de uma solicitação que ficou pendente (sobrevive ao F5).
    const retomar = useCallback(async (id) => {
        solAtivaRef.current = id;
        setProcessando(true);
        const res = await aguardarSolicitacao(id);
        if (solAtivaRef.current !== id) return; // usuário já disparou outra
        setProcessando(false);
        setResultado(res);
        carregarHistorico();
    }, [carregarHistorico]);

    useEffect(() => {
        (async () => {
            const cat = await getCompatibilidadeCatalogos();
            if (cat && cat.data && cat.data.length) setCatalogos(cat.data);
            const hist = await getCompatibilidadeHistorico("manual");
            if (hist && hist.data) {
                setHistorico(hist.data);
                const pend = hist.data.find(h => h.status === "pendente" || h.status === "processando");
                if (pend) retomar(pend.id);
            }
        })();
    }, [retomar]);

    const verificar = async () => {
        const temPeca = form.peca_numero.trim() || form.peca_nome.trim();
        const temVeiculo = form.chassi.trim() || form.modelo.trim() || form.marca.trim();
        if (!temPeca) { toast({ title: "Informe o número ou o nome da peça.", status: "warning", duration: 4000 }); return; }
        if (!temVeiculo) { toast({ title: "Informe o veículo (chassi, ou marca/modelo/ano).", status: "warning", duration: 4000 }); return; }

        setProcessando(true);
        setResultado(null);
        const res = await postCompatibilidadeVerificar(form);
        if (!res || !res.solicitacao_id) {
            setProcessando(false);
            toast({ title: "Não foi possível iniciar a verificação.", description: res?.erro, status: "error", duration: 5000 });
            return;
        }
        carregarHistorico();
        await retomar(res.solicitacao_id);
    };

    const abrirDetalhe = async (row) => {
        const ehEcom = row.tipo === "ecommerce" && !!row.order_number;
        setDetalhe(null);
        setDetalhePedido(null);
        setDetalheEhEcom(ehEcom);
        setDetalheLoading(true);
        setDetalhePedidoLoading(ehEcom);
        onOpen();
        const [sol, ped] = await Promise.all([
            getCompatibilidadeSolicitacao(row.id),
            ehEcom ? getCompatibilidadeSolicitacaoPedido(row.id) : Promise.resolve(null),
        ]);
        setDetalhe(sol || null);
        setDetalheLoading(false);
        if (ehEcom) {
            setDetalhePedido(ped?.pedido || null);
            setDetalhePedidoLoading(false);
        }
    };

    const dispararEcommerce = async () => {
        setEcomVerificando(true);
        const res = await postCompatibilidadeEcommerceVerificar(8);
        setEcomVerificando(false);
        if (!res || res.status !== "ok") {
            toast({ title: "Falha ao disparar a verificação.", status: "error", duration: 4000 });
            return;
        }
        const r = res.resumo || {};
        toast({
            title: "Verificação disparada",
            description: `${r.candidatos || 0} pedido(s): ${r.despachadas || 0} em análise, ${r.fora_cobertura || 0} fora de cobertura, ${r.sem_veiculo || 0} sem veículo.`,
            status: "success", duration: 6000,
        });
        carregarEcommerce();
    };

    const dispararMercadoLivre = async () => {
        setMlVerificando(true);
        const res = await postCompatibilidadeMercadoLivreVerificar(8);
        setMlVerificando(false);
        if (!res || res.status !== "ok") {
            toast({ title: "Falha ao disparar a verificação.", status: "error", duration: 4000 });
            return;
        }
        const r = res.resumo || {};
        toast({
            title: "Verificação disparada",
            description: `${r.candidatos || 0} pedido(s) pendente(s): ${r.despachadas || 0} em análise, ${r.fora_cobertura || 0} fora de cobertura, ${r.sem_chassi || 0} ainda sem chassi.`,
            status: "success", duration: 6000,
        });
        carregarMercadoLivre();
    };

    return (
        <Box p={{ base: 3, md: 6 }} maxW="1200px" mx="auto">
            <HStack mb={1} spacing={2}>
                <Icon as={ShieldCheck} boxSize={6} color="blue.500" />
                <Text fontSize="2xl" fontWeight="bold">Compatibilidade — Catálogo Oficial</Text>
            </HStack>
            <Text color="gray.600" fontSize="sm" mb={3}>
                Confirmação de peça × veículo <b>100% nos catálogos oficiais</b>, com a prancha/vista explodida anexada.
                Pedidos do site e do Mercado Livre: catálogos das montadoras instalados no servidor (Mercedes-Benz, Ford, VW,
                Iveco, Scania, Volvo, Agrale e DAF), sem o partslink24. Verificação manual: partslink24 e, para Iveco, Scania,
                Volvo, Agrale e DAF, o catálogo da própria montadora. Nenhuma outra fonte é usada e nada é inventado — se o
                catálogo não confirmar, o resultado é "não confirmado".
            </Text>

            <Box bg="blue.50" borderWidth="1px" borderColor="blue.200" borderRadius="md" p={3} mb={4}>
                <Text fontSize="xs" fontWeight="bold" color="blue.700" mb={2}>CATÁLOGOS QUE TEMOS ACESSO (só veículos pesados):</Text>
                <Wrap spacing={2}>
                    {catalogos.map(c => (
                        <WrapItem key={c.service}>
                            <Badge colorScheme="blue" variant="subtle" px={2} py={1} borderRadius="md">{c.label}</Badge>
                        </WrapItem>
                    ))}
                </Wrap>
                <Text fontSize="xs" color="gray.600" mt={2}>
                    Veículos de outras marcas (Renault, carros de passeio…) ficam "fora de cobertura" — assim como Volvo antigo
                    (FH12/FH16, NL, VM) e ônibus, que não entram na edição do catálogo Volvo que temos.
                </Text>
            </Box>

            <Tabs colorScheme="blue" variant="enclosed">
                <TabList>
                    <Tab><Icon as={Search} boxSize={4} mr={2} /> Verificação manual</Tab>
                    <Tab><Icon as={Truck} boxSize={4} mr={2} /> Pedidos do e-commerce</Tab>
                    <Tab onClick={() => { if (!mlLista.length) carregarMercadoLivre(); }}>
                        <Icon as={ShoppingCart} boxSize={4} mr={2} /> Pedidos do Mercado Livre
                    </Tab>
                </TabList>

                <TabPanels>
                    {/* ─────────── ABA MANUAL ─────────── */}
                    <TabPanel px={0}>
                        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={5}>
                            {/* Formulário */}
                            <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4}>
                                <Text fontWeight="bold" mb={3}>Dados da verificação</Text>
                                <VStack spacing={3} align="stretch">
                                    <Box>
                                        <Text fontSize="xs" color="gray.600" mb={1}>Catálogo</Text>
                                        <Select size="sm" value={form.catalogo} onChange={e => setCampo("catalogo", e.target.value)}>
                                            <option value="">Detectar automaticamente pela marca</option>
                                            {catalogos.map(c => <option key={c.service} value={c.service}>{c.label}</option>)}
                                        </Select>
                                    </Box>

                                    <Divider />
                                    <Text fontSize="xs" fontWeight="bold" color="gray.500">
                                        VEÍCULO ({dicaChassi(form)})
                                    </Text>
                                    <Input size="sm" value={form.chassi} onChange={e => setCampo("chassi", e.target.value)}
                                           placeholder={phChassi(form)} />
                                    <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={2}>
                                        <Input size="sm" placeholder="Marca (ex.: VW)" value={form.marca} onChange={e => setCampo("marca", e.target.value)} />
                                        <Input size="sm" placeholder={phModelo(form)}
                                               value={form.modelo} onChange={e => setCampo("modelo", e.target.value)} />
                                        <Input size="sm" placeholder="Ano" value={form.ano} onChange={e => setCampo("ano", e.target.value)} />
                                    </SimpleGrid>
                                    {ehAgrale(form) && (
                                        <Box bg="orange.50" borderWidth="1px" borderColor="orange.200" borderRadius="md" p={2}>
                                            <HStack align="start" spacing={2}>
                                                <Icon as={AlertTriangle} boxSize={3.5} color="orange.500" mt="2px" />
                                                <Text fontSize="xs" color="orange.800">
                                                    A Agrale não publica banco de chassis: a confirmação sai em <b>nível de modelo</b> (a peça
                                                    consta, ou não, nas pranchas do catálogo daquele modelo). Informe o modelo o mais completo
                                                    possível — <b>MA 9.2 Euro V</b>, <b>Volare W9</b>, <b>Marruá AM 200</b> — porque cada geração
                                                    tem catálogo próprio.
                                                </Text>
                                            </HStack>
                                        </Box>
                                    )}
                                    {ehDaf(form) && (
                                        <Box bg="blue.50" borderWidth="1px" borderColor="blue.200" borderRadius="md" p={2}>
                                            <HStack align="start" spacing={2}>
                                                <Icon as={AlertTriangle} boxSize={3.5} color="blue.500" mt="2px" />
                                                <Text fontSize="xs" color="blue.800">
                                                    O chassi DAF tem <b>8 caracteres</b> (ex.: <b>0E653487</b>) — não é o VIN de 17. Com ele a
                                                    confirmação sai no <b>nível do veículo</b> (o conjunto exato com que o caminhão saiu de
                                                    fábrica); sem ele, só em nível de modelo. A edição do catálogo é de <b>2015</b>: caminhão mais
                                                    novo pode não constar. Os textos do catálogo são em inglês — a resposta vem traduzida.
                                                </Text>
                                            </HStack>
                                        </Box>
                                    )}

                                    <Divider />
                                    <Text fontSize="xs" fontWeight="bold" color="gray.500">PEÇA</Text>
                                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                                        <Input size="sm" placeholder="Número (ex.: 2Z0141031P)" value={form.peca_numero} onChange={e => setCampo("peca_numero", e.target.value)} />
                                        <Input size="sm" placeholder="Nome (ex.: disco de embreagem)" value={form.peca_nome} onChange={e => setCampo("peca_nome", e.target.value)} />
                                    </SimpleGrid>

                                    <Textarea size="sm" rows={2} placeholder="Contexto adicional (opcional)"
                                              value={form.contexto} onChange={e => setCampo("contexto", e.target.value)} />

                                    <HStack>
                                        <Button colorScheme="blue" size="sm" leftIcon={<Icon as={Search} boxSize={4} />}
                                                onClick={verificar} isLoading={processando} loadingText="Consultando catálogo…">
                                            Verificar compatibilidade
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => { setForm(FORM_VAZIO); setResultado(null); }} isDisabled={processando}>
                                            Limpar
                                        </Button>
                                    </HStack>
                                </VStack>
                            </Box>

                            {/* Resultado */}
                            <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={4} minH="200px">
                                <Text fontWeight="bold" mb={3}>Resultado</Text>
                                {processando && (
                                    <VStack py={8} color="gray.500" spacing={3}>
                                        <Spinner size="lg" color="blue.400" />
                                        <Text fontSize="sm">Consultando o catálogo oficial… pode levar 1–2 minutos.</Text>
                                        <Text fontSize="xs">Pode sair desta tela — a verificação continua e fica no histórico.</Text>
                                    </VStack>
                                )}
                                {!processando && !resultado && (
                                    <VStack py={8} color="gray.400" spacing={2}>
                                        <Icon as={FileSearch} boxSize={8} />
                                        <Text fontSize="sm">Preencha os dados e clique em verificar.</Text>
                                    </VStack>
                                )}
                                {!processando && resultado && <ResultadoDetalhe sol={resultado} />}
                            </Box>
                        </SimpleGrid>

                        {/* Histórico */}
                        <Box mt={6}>
                            <HStack mb={2} justify="space-between">
                                <Text fontWeight="bold" fontSize="sm">Verificações recentes</Text>
                                <Button size="xs" variant="ghost" leftIcon={<Icon as={RefreshCw} boxSize={3} />} onClick={carregarHistorico}>Atualizar</Button>
                            </HStack>
                            <VStack align="stretch" spacing={1}>
                                {historico.length === 0 && <Text fontSize="sm" color="gray.400">Nenhuma verificação ainda.</Text>}
                                {historico.map(h => (
                                    <Flex key={h.id} p={2} borderWidth="1px" borderColor="gray.100" borderRadius="md" align="center"
                                          justify="space-between" _hover={{ bg: "gray.50" }} cursor="pointer" onClick={() => abrirDetalhe(h)}>
                                        <Box minW={0}>
                                            <Text fontSize="sm" noOfLines={1}><b>{h.peca || "—"}</b> · {h.veiculo || "—"}</Text>
                                            <Text fontSize="xs" color="gray.500">{formatarData(h.created_at)}</Text>
                                        </Box>
                                        <StatusBadge status={h.status} veredito={h.veredito} />
                                    </Flex>
                                ))}
                            </VStack>
                        </Box>
                    </TabPanel>

                    {/* ─────────── ABA E-COMMERCE ─────────── */}
                    <TabPanel px={0}>
                        <PainelPedidos
                            lista={ecomLista}
                            loading={ecomLoading}
                            verificando={ecomVerificando}
                            onAtualizar={carregarEcommerce}
                            onVerificar={dispararEcommerce}
                            onAbrir={abrirDetalhe}
                            IconeVazio={Truck}
                            botaoLabel="Verificar pedidos agora"
                            aviso={<>Roda <b>automaticamente a cada 30 min</b> nos pedidos recentes com dados de veículo. O resultado vira uma nota na venda.</>}
                            vazio={'Nenhum pedido verificado ainda. Clique em "Verificar pedidos agora" ou aguarde o ciclo automático.'}
                        />
                    </TabPanel>

                    {/* ─────────── ABA MERCADO LIVRE ─────────── */}
                    <TabPanel px={0}>
                        <PainelPedidos
                            ehML
                            lista={mlLista}
                            loading={mlLoading}
                            verificando={mlVerificando}
                            onAtualizar={carregarMercadoLivre}
                            onVerificar={dispararMercadoLivre}
                            onAbrir={abrirDetalhe}
                            IconeVazio={ShoppingCart}
                            botaoLabel="Verificar pendentes agora"
                            aviso={<>Roda <b>automaticamente a cada 30 min</b> nos pedidos do ML travados em <b>pendente</b> (acima de R$ 2.500, à espera do chassi). O chassi é procurado na observação do pedido, no chat do ML e na <b>foto do documento</b> que o cliente mandou. Compatível com todos os itens conferidos, o pedido é <b>confirmado sozinho</b>; qualquer outro resultado só vira nota e o pedido segue pendente.</>}
                            vazio={'Nenhum pedido pendente verificado ainda. Clique em "Verificar pendentes agora" ou aguarde o ciclo automático.'}
                        />
                    </TabPanel>
                </TabPanels>
            </Tabs>

            {/* Modal de detalhe */}
            <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">{detalheEhEcom ? "Detalhe do pedido" : "Detalhe da verificação"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        {detalheEhEcom && (
                            <>
                                <PedidoResumo pedido={detalhePedido} loading={detalhePedidoLoading} />
                                <Divider my={4} />
                                <HStack mb={3} spacing={2}>
                                    <Icon as={ShieldCheck} boxSize={4} color="blue.500" />
                                    <Text fontWeight="bold" fontSize="sm">Resultado da compatibilidade</Text>
                                </HStack>
                            </>
                        )}
                        {detalheLoading && <VStack py={8}><Spinner /><Text fontSize="sm" color="gray.500">Carregando…</Text></VStack>}
                        {!detalheLoading && detalhe && (detalhe.status === "pendente" || detalhe.status === "processando") && (
                            <VStack py={8} color="gray.500" spacing={2}><Spinner /><Text fontSize="sm">Ainda em processamento.</Text></VStack>
                        )}
                        {!detalheLoading && detalhe && detalhe.status !== "pendente" && detalhe.status !== "processando" && (
                            <ResultadoDetalhe sol={detalhe} />
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default CompatibilidadeCatalogo;
